import { OverlayContainer } from "@angular/cdk/overlay";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  inject
} from "@angular/core";
import {
  ActivationEnd,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet
} from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { select, Store } from "@ngrx/store";
import { BehaviorSubject, Subject, firstValueFrom } from "rxjs";
import { AuthService } from "@app/core/auth/auth.service";
import { takeUntil } from "rxjs/operators";
import {
  ActionAuthLogout,
  AnimationsService,
  routeAnimations,
  selectorAuth,
  TitleService
} from "@app/core";
import { environment as env } from "@env/environment";

import { NIGHT_MODE_THEME, selectorSettings, SettingsState } from "./settings";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { HttpClient } from "@angular/common/http";
import { ServerStatusDialogComponent } from "@app/view/shared/server-status-dialog/server-status-dialog.component";
import { UpdateService } from "@app/services/client/update.service";
import { UserService } from "@app/services/rest/user.service";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { MsalService } from "@azure/msal-angular";
import { AuthenticationResult } from "@azure/msal-browser";
import { TelemetryProviderService } from "@app/core/telemetry/telemetry-provider.service";
import { StateProvider } from "@bryntum/core-thin";
import { NgxUiLoaderModule } from "ngx-ui-loader";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatToolbar } from "@angular/material/toolbar";
import { StompStatusComponent } from "./view/shared/widgets/stomp-status/stomp-status.component";
import { FollowOnToolComponent } from "./view/shared/alerts/follow-on-tool/follow-on-tool.component";
import { RecallToolComponent } from "./view/shared/alerts/recall-tool/recall-tool.component";
import { ReservationAlertToolComponent } from "./view/shared/alerts/reservation-alert-tool/reservation-alert-tool.component";
import { HeldTicketsToolComponent } from "./view/shared/alerts/held-tickets-tool/held-tickets-tool.component";
import { AlertToolComponent } from "./view/shared/alerts/alert-tool/alert-tool.component";
import { SolverStatusWidgetComponent } from "./view/planning/solver-status-widget/solver-status-widget.component";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatIcon } from "@angular/material/icon";
import { MomentModule } from "ngx-moment";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  animations: [routeAnimations],
  imports: [
    NgxUiLoaderModule,
    MatButton,
    MatToolbar,
    StompStatusComponent,
    RouterLinkActive,
    RouterLink,
    FollowOnToolComponent,
    RecallToolComponent,
    ReservationAlertToolComponent,
    HeldTicketsToolComponent,
    AlertToolComponent,
    SolverStatusWidgetComponent,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    RouterOutlet,
    MomentModule,
    TranslatePipe
  ]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  overlayContainer = inject(OverlayContainer);
  private store = inject<Store<any>>(Store);
  private router = inject(Router);
  private titleService = inject(TitleService);
  private animationService = inject(AnimationsService);
  private translate = inject(TranslateService);
  private msalService = inject(MsalService);
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  updateService = inject(UpdateService);
  userService = inject(UserService);
  private clientConfigService = inject(ClientConfigService);
  private telemetryProviderService = inject(TelemetryProviderService);
  private authService = inject(AuthService);

  @HostBinding("class") componentCssClass;
  version = env.versions.app;
  serverVersion: string = "Please wait...";
  navigation = [];
  navigationSideMenu = [...this.navigation];
  serverAvailable = new BehaviorSubject<boolean>(true);
  settings: SettingsState;
  isHideMenu: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isPlanner: boolean;
  isViewer: boolean;
  allowResourceLocking: boolean;
  allowForceBooking: boolean;
  allowPriorityReservation: boolean;
  isRegistered: boolean;
  time: number = Date.now();
  isAuthenticating: boolean;
  userDisplayName: string;
  telemetryEnabled: boolean;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private serverDialog: MatDialogRef<ServerStatusDialogComponent, any>;
  private intervalId: any;

  // Track document visibility changes to handle token refreshes when app becomes visible again
  private lastVisibilityChange: number = Date.now();

  @HostListener("document:visibilitychange")
  onVisibilityChange() {
    const now = Date.now();
    const timeSinceLastChange = now - this.lastVisibilityChange;
    this.lastVisibilityChange = now;

    // Only handle if returning to visible state after significant time away (30+ seconds)
    if (document.visibilityState === "visible" && timeSinceLastChange > 30000) {
      console.log(
        "[App] Page became visible after inactivity:",
        timeSinceLastChange / 1000,
        "seconds"
      );

      // Reset auth cancelled state to allow new authentication attempts
      this.authService.resetCancelledState();

      // Only try to refresh token if we're authenticated
      if (this.isAuthenticated) {
        console.log(
          "[App] Preemptively refreshing authentication state after inactivity"
        );

        // Use the authenticated service to refresh tokens safely
        this.authService
          .refreshAuthenticationState()
          .then((success) => {
            if (success) {
              console.log(
                "[App] Authentication state refreshed successfully after inactivity"
              );
            } else {
              console.log(
                "[App] Authentication state refresh failed after inactivity"
              );
            }
          })
          .catch((error) => {
            console.error(
              "[App] Error refreshing authentication after inactivity:",
              error
            );
          });
      }
    }
  }

  constructor() {
    const translate = this.translate;
    const msalService = this.msalService;

    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang("en");
    StateProvider.setup("local");
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    translate.use("en");
    this.intervalId = setInterval(() => {
      this.time = Date.now();
    }, 1000);

    // console.log("syncing client config");
    console.log("MSAL", msalService);
  }

  ngAfterViewInit(): void {
    this.serverAvailable.subscribe((isAvailable) => {
      if (this.isAuthenticated) {
        if (isAvailable) {
          if (this.serverDialog) {
            this.serverDialog.close();
          }
        } else {
          if (!this.serverDialog) {
            this.serverDialog = this.dialog.open(ServerStatusDialogComponent, {
              // disableClose: true
            });
            this.serverDialog.componentInstance.onRetry.subscribe(() => {
              this.getServerVersion(
                this.serverDialog.componentInstance.onLoading
              );
            });
          }
        }
      }
    });
  }

  /**
   * Handle an authentication result - delegates to AuthService and performs app-specific tasks
   */
  handleAuthResult(authResult: AuthenticationResult) {
    // Prevent duplicate processing
    if (this.handlingAuthResult) {
      console.log(
        "[App] Already processing an auth result, skipping duplicate"
      );
      return;
    }

    this.handlingAuthResult = true;
    console.log("[App] Handling authentication result");

    try {
      // Set basic UI state
      this.isAuthenticating = true;
      this.userDisplayName = authResult.account.name;
      this.getServerVersion();

      // Delegate the auth state processing to the AuthService
      this.authService.processAuthResultAndUpdateState(authResult);

      // Setup navigation based on user roles (this depends on the auth state)
      this.setupNavigation();

      // Check registration status
      this.userService.getPlanner().then(
        () => {
          this.isRegistered = true;
          this.handlingAuthResult = false;
        },
        (error) => {
          console.log("[App] Error getting planner:", error);

          // Check if this is specifically a "not registered" error
          if (error.isNotRegistered) {
            console.log(
              "[App] User is not registered, redirecting to registration"
            );
            this.isRegistered = false;
            this.navigation = [];
            this.router.navigate(["registration"]).then();
          } else if (error.status !== 0 && error.status !== 504) {
            // Handle other non-network errors
            console.log(
              "[App] Other planner error, redirecting to registration"
            );
          } else {
            // Network error - don't redirect to registration
            console.log("[App] Network error getting planner, not redirecting");
          }
          this.handlingAuthResult = false;
        }
      );
    } catch (error) {
      console.error("[App] Error handling auth result:", error);
      this.handlingAuthResult = false;
    }
  }

  /**
   * Sets up the navigation menu based on authenticated user roles
   */
  private setupNavigation() {
    console.log("[App] Setting up navigation based on user roles");

    // Only proceed if user is authenticated
    if (!this.isAuthenticated) {
      console.log("[App] User not authenticated, skipping navigation setup");
      return;
    }

    // Reset navigation to base items for all authenticated users
    this.navigation = [
      { link: "view", label: "scheduler.menu.data-viewer" },
      { link: "diary", label: "scheduler.menu.diary" },
      { link: "planning", label: "scheduler.menu.schedule" }
    ];

    // Add role-specific navigation items
    if (this.isAdmin || this.isPlanner) {
      this.navigation.push({
        link: "booking",
        label: "scheduler.menu.booking"
      });
    }
    if (this.isAdmin) {
      this.navigation.push({ link: "setup", label: "scheduler.menu.setup" });
    }

    // Update side menu with new navigation
    this.navigationSideMenu = [
      ...this.navigation,
      { link: "settings", label: "scheduler.menu.settings" }
    ];

    console.log("[App] Navigation setup complete:", this.navigation);
  }

  // Track if we're currently handling an auth result to prevent duplicate processing
  private handlingAuthResult = false;

  /**
   * Helper method to check for accounts and handle authentication state refresh
   */
  private checkAccounts(): void {
    console.log("[App] Checking for existing accounts");

    // Start refreshing auth state in the background, don't wait for it
    if (!this.handlingAuthResult) {
      setTimeout(() => {
        try {
          // Fast check for accounts first
          const accounts = this.msalService.instance.getAllAccounts();

          if (accounts && accounts.length > 0) {
            console.log("[App] Found existing accounts, refreshing auth state");

            this.authService
              .refreshAuthenticationState()
              .then((success) => {
                console.log(
                  "[App] Background auth refresh:",
                  success ? "succeeded" : "failed"
                );

                // If successful and we have accounts but UI doesn't show authenticated,
                // we may need to process auth manually
                if (success && !this.isAuthenticated) {
                  // Get token to trigger auth state update
                  this.msalService
                    .acquireTokenSilent({
                      scopes: ["openid", "profile", "email", "offline_access"],
                      account: accounts[0]
                    })
                    .subscribe({
                      next: (result) => {
                        console.log(
                          "[App] Silent token acquisition successful"
                        );
                        this.handleAuthResult(result);
                      },
                      error: (error) => {
                        console.log(
                          "[App] Silent token acquisition failed:",
                          error
                        );
                      }
                    });
                }
              })
              .catch((e) => {
                console.error("[App] Background auth refresh error:", e);
              });
          } else {
            console.log("[App] No accounts found during startup");

            // Reconcile a stale persisted auth state: the ngrx store rehydrates
            // isAuthenticated from localStorage (scheduler-AUTH) independently of
            // MSAL. If the store says we're authenticated but MSAL has no account
            // (token expired/cleared, or a different tenant), the app would render
            // the authenticated shell and 401 on every request forever. Reset to
            // logged-out so the login screen shows. We deliberately do NOT
            // auto-trigger loginRedirect here to avoid any risk of a redirect loop.
            //
            // The getAllAccounts() above can be empty before MSAL finishes
            // initializing, so confirm via safeGetAllAccounts() (which awaits
            // initialization) and re-check isAuthenticated before logging out —
            // otherwise we could wrongly reset a still-valid session at startup.
            if (this.isAuthenticated) {
              this.authService
                .safeGetAllAccounts()
                .then((confirmedAccounts) => {
                  if (
                    this.isAuthenticated &&
                    (!confirmedAccounts || confirmedAccounts.length === 0)
                  ) {
                    console.warn(
                      "[App] Auth state mismatch — store authenticated but no MSAL account; resetting to logged-out"
                    );
                    this.store.dispatch(new ActionAuthLogout());
                  }
                })
                .catch((e) =>
                  console.log("[App] Error confirming accounts:", e)
                );
            }
          }
        } catch (e) {
          console.error("[App] Error checking accounts:", e);
        }
      }, 1000);
    }
  }

  ngOnInit(): void {
    this.translate.setDefaultLang("en");
    this.subscribeToSettings();
    this.subscribeToIsAuthenticated();
    this.subscribeToRouterEvents();

    if (this.clientConfigService.config.applicationInsights.mode === "force") {
      this.telemetryEnabled = true;
    }
    if (
      this.clientConfigService.config.applicationInsights.mode === "optional" &&
      window.localStorage.getItem("telemetryOptIn") === "true"
    ) {
      this.telemetryEnabled = true;
    }

    if (this.telemetryEnabled) {
      // Try synchronous check first for faster initialization
      const isAuthenticated = this.authService.isCurrentlyAuthenticated();

      if (isAuthenticated) {
        // Get accounts directly if possible
        try {
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts && accounts.length > 0) {
            this.telemetryProviderService.init(
              accounts[0].username || "unknown"
            );
          } else {
            this.telemetryProviderService.init("unknown");
          }
        } catch (e) {
          // Fall back to async method if direct access fails
          this.authService
            .safeGetAllAccounts()
            .then((accounts) => {
              if (accounts && accounts.length > 0) {
                this.telemetryProviderService.init(accounts[0].username);
              } else {
                this.telemetryProviderService.init("unknown");
              }
            })
            .catch(() => {
              this.telemetryProviderService.init("unknown");
            });
        }
      } else {
        // Fallback to async check in case sync check was too early
        this.authService
          .checkAuthenticationStateAsync()
          .then((isAuthAsync) => {
            if (isAuthAsync) {
              this.authService
                .safeGetAllAccounts()
                .then((accounts) => {
                  if (accounts && accounts.length > 0) {
                    this.telemetryProviderService.init(accounts[0].username);
                  } else {
                    this.telemetryProviderService.init("unknown");
                  }
                })
                .catch(() => {
                  this.telemetryProviderService.init("unknown");
                });
            } else {
              this.telemetryProviderService.init("unknown");
            }
          })
          .catch(() => {
            this.telemetryProviderService.init("unknown");
          });
      }
    }

    console.log("[App] Starting authentication initialization");

    // Clear any stale interaction state that might be causing login loops,
    // but ONLY if we are NOT handling a redirect response from Azure.
    // MSAL needs msal.interaction.status to correlate the auth response with
    // the original auth request.  Clearing it prematurely causes
    // handleRedirectObservable to return null → infinite redirect loop.
    // Check both the hash (responseMode "fragment") and the query string
    // (responseMode "query") — a query-mode callback has an empty hash.
    // Reuses AuthService's boundary-aware matcher, which recognises code/
    // id_token/access_token and error callbacks (so interaction status isn't
    // cleared prematurely) without false-positiving on params like ?postcode=.
    const authResponseInUrl = `${window.location.hash}${window.location.search}`;
    const hasAuthRedirectInUrl =
      AuthService.urlHasAuthResponseParams(authResponseInUrl);

    if (!hasAuthRedirectInUrl) {
      try {
        localStorage.removeItem("msal.interaction.status");
        sessionStorage.removeItem("msal.interaction.status");
      } catch (e) {
        console.log("[App] Error clearing interaction status:", e);
      }
    } else {
      console.log(
        "[App] Auth redirect detected in URL — preserving interaction status"
      );
    }

    // Whether we're coming back from a redirect (auth response present in the
    // hash for "fragment" mode, or the query string for "query" mode). Same
    // criteria as the interaction-status guard above, including error callbacks.
    const isRedirectCallback = hasAuthRedirectInUrl;

    console.log(
      `[App] Detected ${
        isRedirectCallback ? "potential" : "no"
      } redirect callback in URL`
    );

    // Don't wait for initialization to complete - just start it in parallel
    this.authService.ensureInitialized();

    // Handle redirect flow first - this is the most important for login completion
    console.log("[App] Handling redirect responses using AuthService");

    // Check for MSAL's redirectHash property which indicates a redirect response.
    // Only treat it as a callback when it actually carries auth-response params —
    // on a hash-routed deep link redirectHash holds the Angular route (e.g.
    // "#/view?v=Job"), which is NOT a redirect response. Treating that as one
    // ran the fallback handleRedirectObservable() concurrently with the guard's
    // loginRedirect, corrupting MSAL's request cache and causing an Empty code
    // exchange (endless login loop).
    const msalRedirectHash: string =
      (this.msalService as any).redirectHash || "";
    const hasMsalRedirectHash =
      msalRedirectHash.includes("code=") ||
      msalRedirectHash.includes("id_token") ||
      msalRedirectHash.includes("access_token") ||
      msalRedirectHash.includes("error=");
    console.log(`[App] MSAL redirect hash detected: ${hasMsalRedirectHash}`);

    // First try our new AuthService method
    this.authService.handleRedirectResponse().then(
      (authResult) => {
        console.log(
          "[App] AuthService redirect handler result:",
          authResult ? "success" : "no result"
        );

        // If authResult exists, process it to ensure role-based permissions are set correctly
        if (authResult) {
          console.log(
            "[App] Processing auth result from redirect using AuthService"
          );
          this.authService.processAuthResultAndUpdateState(authResult);

          // After processing, check if we're actually authenticated in the state
          setTimeout(() => {
            console.log("[App] Post-processing auth state check:", {
              isAuthenticated: this.isAuthenticated,
              isAdmin: this.isAdmin,
              isPlanner: this.isPlanner,
              isViewer: this.isViewer
            });
          }, 500);
        }
        // If our service didn't handle the redirect but we may have a response in the hash
        else if (isRedirectCallback || hasMsalRedirectHash) {
          console.log(
            "[App] Trying MSAL's redirect handler directly as fallback"
          );

          // Try MSAL's handler directly as a fallback
          // navigateToLoginRequestUrl:false — see AuthService.handleRedirectResponse;
          // in MSAL v5 this only takes effect as a per-call option, not auth config.
          this.msalService
            .handleRedirectObservable({ navigateToLoginRequestUrl: false })
            .subscribe({
              next: (result) => {
                if (result) {
                  console.log(
                    "[App] MSAL direct redirect handler got a result"
                  );
                  this.handleAuthResult(result);
                } else {
                  console.log(
                    "[App] MSAL direct redirect handler got no result"
                  );
                  this.checkAccounts();
                }
              },
              error: (error) => {
                console.error(
                  "[App] Error in MSAL direct redirect handling:",
                  error
                );
                // Even on error, check if we have any accounts
                this.checkAccounts();
              }
            });
        } else if (!authResult) {
          // Just check accounts as normal if no redirect was expected
          this.checkAccounts();
        }
      },
      (error) => {
        console.error("[App] Error in AuthService redirect handling:", error);

        // Try MSAL's handler directly as a fallback
        console.log(
          "[App] Trying MSAL's redirect handler directly after AuthService error"
        );
        // navigateToLoginRequestUrl:false — see AuthService.handleRedirectResponse;
        // in MSAL v5 this only takes effect as a per-call option, not auth config.
        this.msalService
          .handleRedirectObservable({ navigateToLoginRequestUrl: false })
          .subscribe({
            next: (result) => {
              if (result) {
                this.handleAuthResult(result);
              } else {
                this.checkAccounts();
              }
            },
            error: (secondError) => {
              console.error(
                "[App] Second error in redirect handling:",
                secondError
              );
              this.checkAccounts();
            }
          });
      }
    );

    // Ensure navigation is properly initialized if the user is already authenticated
    if (this.isAuthenticated && this.navigation.length === 0) {
      console.log(
        "[App] User already authenticated at startup, initializing navigation"
      );
      this.setupNavigation();
    } else if (this.isAuthenticated) {
      // User is authenticated but navigation is already set up
      console.log("[App] User already authenticated, checking server version");
      this.getServerVersion();
    } else {
      this.navigationSideMenu = [...this.navigation];
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  async onLoginClick() {
    console.log("[App] Login requested, using AuthService login method");

    try {
      // Use the AuthService login method which implements redirect flow
      await this.authService.login();
    } catch (error) {
      console.error("[App] Error during login:", error);
    }
  }

  async onLogoutClick() {
    console.log("[App] Logout requested");

    // Dispatch logout action to store immediately
    this.store.dispatch(new ActionAuthLogout());

    try {
      // Try to initialize but don't block logout
      await Promise.race([
        this.authService.ensureInitialized(),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(true), 500))
      ]);

      // Proceed with logout
      await firstValueFrom(this.msalService.logoutRedirect());
    } catch (error) {
      console.error("[App] Error during logout:", error);

      // Try logout anyway
      await firstValueFrom(this.msalService.logoutRedirect());
    }
  }

  private getServerVersion(relayLoadState: EventEmitter<boolean> = null) {
    if (relayLoadState) {
      relayLoadState.emit(true);
    }
    this.http
      .get(this.clientConfigService.config.serviceUrl + "/sysInfo", {
        withCredentials: true
      })
      .subscribe(
        (value: any) => {
          this.serverAvailable.next(true);
          this.serverVersion = value.version;

          if (relayLoadState) {
            relayLoadState.emit(false);
          }
        },
        (errorMessage) => {
          this.serverAvailable.next(false);
          console.log(errorMessage);
          if (errorMessage.status === 401) {
          }
          if (relayLoadState) {
            relayLoadState.emit(false);
          }
        }
      );
  }

  private subscribeToIsAuthenticated() {
    this.store
      .pipe(select(selectorAuth))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((auth) => {
        console.log("Auth changed");
        console.log(auth);

        const wasAuthenticated = this.isAuthenticated;
        this.isAuthenticated = auth.isAuthenticated;
        this.isAdmin = auth.isAdmin;
        this.isPlanner = auth.isPlanner;
        this.isViewer = auth.isViewer;
        this.allowResourceLocking = auth.allowResourceLocking;
        this.allowForceBooking = auth.allowForceBooking;
        this.allowPriorityReservation = auth.allowPriorityReservation;
        this.userDisplayName = auth.fullName;

        // If user became authenticated or was already authenticated with role information
        if (
          this.isAuthenticated &&
          (!wasAuthenticated || this.isAdmin !== undefined)
        ) {
          this.setupNavigation();

          // Check registration status whenever user becomes authenticated
          // This ensures we catch cases where user navigates directly to pages that need planner data
          if (!wasAuthenticated && !this.handlingAuthResult) {
            console.log(
              "[App] User became authenticated, checking registration status"
            );
            this.checkRegistrationStatus();
          }
        }
      });
  }

  /**
   * Check if the user is registered by calling the planner endpoint
   */
  private checkRegistrationStatus() {
    this.userService.getPlanner().then(
      () => {
        console.log("[App] User is registered");
        this.isRegistered = true;
      },
      (error) => {
        console.log(
          "[App] Error getting planner during registration check:",
          error
        );

        // Check if this is specifically a "not registered" error
        if (error.isNotRegistered) {
          console.log(
            "[App] User is not registered, redirecting to registration"
          );
          this.isRegistered = false;
          this.navigation = [];
          this.router.navigate(["registration"]).then();
        } else if (error.status !== 0 && error.status !== 504) {
          // Handle other non-network errors
          console.log(
            "[App] Other planner error during registration check, redirecting to registration"
          );
        } else {
          // Network error - don't redirect to registration
          console.log(
            "[App] Network error getting planner during registration check, not redirecting"
          );
        }
      }
    );
  }

  private subscribeToSettings() {
    this.store
      .pipe(select(selectorSettings))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((settings) => {
        this.settings = settings;
        this.setTheme(settings);
        this.setLanguage(settings);
        this.animationService.updateRouteAnimationType(
          settings.pageAnimations,
          settings.elementsAnimations
        );
      });
  }

  private setTheme(settings: SettingsState) {
    const { theme, autoNightMode } = settings;
    const hours = new Date().getHours();
    const effectiveTheme = (
      autoNightMode && (hours >= 20 || hours <= 6) ? NIGHT_MODE_THEME : theme
    ).toLowerCase();
    this.componentCssClass = effectiveTheme;
    const classList = this.overlayContainer.getContainerElement().classList;
    const toRemove = Array.from(classList).filter((item: string) =>
      item.includes("-theme")
    );
    if (toRemove.length) {
      classList.remove(...toRemove);
    }
    classList.add(effectiveTheme);
  }

  private setLanguage(settings: SettingsState) {
    const { language } = settings;
    if (language) {
      this.translate.use(language);
    }
  }

  private subscribeToRouterEvents() {
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event instanceof ActivationEnd) {
        this.isHideMenu = event.snapshot.data.hideMenu;
        this.titleService.setTitle(event.snapshot);
      }

      // Handle post-navigation authentication state refresh
      if (event instanceof NavigationEnd) {
        // Check if we've just returned from authentication
        const isAuthRelatedRoute =
          event.url === "/" &&
          document.referrer.includes("login.microsoftonline.com");

        if (isAuthRelatedRoute) {
          console.log(
            "[App] Detected return from authentication, refreshing state"
          );

          // Refresh authentication state after slight delay to ensure MSAL has processed the auth result
          setTimeout(() => {
            if (this.msalService.instance.getAllAccounts().length > 0) {
              // If we have an account, try to refresh connections silently
              this.refreshAppState();
            }
          }, 500);
        }
      }
    });
  }

  // Refresh app state after authentication changes
  private refreshAppState() {
    console.log(
      "[App] Refreshing application state after authentication change"
    );

    // Get fresh server version to test connectivity
    this.getServerVersion();

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      // If we have account info but the auth state doesn't reflect it,
      // we may need to process the auth result manually
      if (!this.isAuthenticated) {
        console.log(
          "[App] Accounts found but not authenticated in UI state - forcing authentication"
        );

        // Set the active account regardless to ensure it's properly set
        try {
          console.log("[App] Setting active account");
          this.msalService.instance.setActiveAccount(accounts[0]);

          // Force-create an auth result and process it - using any type to bypass strict typing
          // This is just to update the UI state, we're not using the token for anything
          const forceAuthResult: any = {
            account: accounts[0],
            accessToken: "dummy-token", // We don't actually use this directly
            tokenType: "Bearer",
            expiresOn: new Date(Date.now() + 3600 * 1000),
            scopes: ["openid", "profile"],
            authority: "https://login.microsoftonline.com/common",
            uniqueId: accounts[0].homeAccountId || "unknown",
            tenantId: accounts[0].tenantId || "unknown",
            idToken: "dummy-id-token",
            correlationId: "dummy-correlation-id"
          };

          // Directly process the auth result to update the state
          console.log("[App] Force-processing auth result with account");
          this.handleAuthResult(forceAuthResult);

          // As a backup, also try silent token acquisition
          if (!this.handlingAuthResult) {
            this.handlingAuthResult = true;

            try {
              console.log(
                "[App] Attempting silent token acquisition with account"
              );

              this.msalService
                .acquireTokenSilent({
                  scopes: ["openid", "profile", "email", "offline_access"],
                  account: accounts[0]
                })
                .subscribe({
                  next: (result) => {
                    console.log("[App] Silent token acquisition successful");
                    this.handleAuthResult(result);
                    this.handlingAuthResult = false;
                  },
                  error: (error) => {
                    console.log(
                      "[App] Silent token acquisition failed:",
                      error
                    );
                    this.handlingAuthResult = false;
                  }
                });
            } catch (e) {
              console.error("[App] Error during token acquisition:", e);
              this.handlingAuthResult = false;
            }
          }
        } catch (e) {
          console.error("[App] Error setting active account:", e);

          // Use our auth service as a fallback
          this.authService
            .refreshAuthenticationState()
            .then((success) => {
              console.log(
                "[App] Auth state refresh:",
                success ? "succeeded" : "failed"
              );
            })
            .catch((err) => {
              console.error("[App] Error refreshing auth state:", err);
            });
        }
      } else {
        // User is already authenticated, ensure navigation is properly set up
        console.log(
          "[App] User already authenticated, ensuring navigation is set up"
        );
        this.setupNavigation();
      }
    }
  }
}
